import { useEffect, useRef, useState } from 'react';
import { Building2, CheckCircle2, Copy, CreditCard, ImagePlus, LockKeyhole, Plus, Ticket, X } from 'lucide-react';
import { apiRequest, apiUploadFile, ApiError } from '../../api/http';
import { MerchantRow as Row } from './merchantAccess';

type Method = 'transfer' | 'card' | 'coupon';
type Query = { user_id: string; merchant_id: string; place_id: string };
type SavedCard = { saved_card_id: string; brand?: string; last_digits?: string; expiration_month?: number; expiration_year?: number; name?: string };
type PaymentMethods = { cards?: SavedCard[]; omise?: { enabled?: boolean; public_key?: string } };
type CardPayment = { payment_id: string; status: string; paid: boolean; authorize_uri?: string; failure_message?: string };

const presets = [990, 3900, 6900];
const terminalStatuses = new Set(['failed', 'expired', 'reversed']);
const wait = (ms: number) => new Promise(resolve => window.setTimeout(resolve, ms));
const errorMessage = (reason: unknown) => reason instanceof ApiError ? reason.message : reason instanceof Error ? reason.message : 'เติมเงินไม่สำเร็จ กรุณาลองอีกครั้ง';
const securityCodeLength = (cardNumber: string) => /^3[47]/.test(cardNumber.replace(/\D/g, '')) ? 4 : 3;

async function tokenizeCard(publicKey: string, card: { name: string; number: string; month: string; year: string; cvc: string }) {
  if (!publicKey.trim()) throw new Error('ยังไม่สามารถชำระด้วยบัตรได้ในขณะนี้');
  const body = new URLSearchParams({
    'card[name]': card.name.trim(),
    'card[number]': card.number.replace(/\D/g, ''),
    'card[expiration_month]': card.month,
    'card[expiration_year]': card.year.length === 2 ? `20${card.year}` : card.year,
    'card[security_code]': card.cvc,
  });
  const response = await fetch('https://vault.omise.co/tokens', {
    method: 'POST',
    headers: { Authorization: `Basic ${btoa(`${publicKey.trim()}:`)}`, 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  const result = await response.json().catch(() => ({})) as { id?: string; message?: string };
  if (!response.ok || !result.id) throw new Error(result.message || 'ตรวจสอบบัตรไม่สำเร็จ');
  return result.id;
}

export function MerchantTopUpModal({ place, wallet, query, onClose, onSuccess }: {
  place: Row;
  wallet: Row;
  query: Query;
  onClose: () => void;
  onSuccess: (message: string) => Promise<void> | void;
}) {
  const [method, setMethod] = useState<Method>('transfer');
  const funding = new URLSearchParams(location.search);
  const hasFundingContext = funding.get('place_id') === query.place_id;
  const salesRefId = hasFundingContext ? funding.get('sales_ref_id') || '' : '';
  const packageId = hasFundingContext ? funding.get('package_id') || '' : '';
  const [amount, setAmount] = useState(hasFundingContext ? funding.get('funding_amount') || '' : '');
  const [coupon, setCoupon] = useState('');
  const [note, setNote] = useState('');
  const [slip, setSlip] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [methods, setMethods] = useState<PaymentMethods | null>(null);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [selectedCard, setSelectedCard] = useState('');
  const [saveCard, setSaveCard] = useState(true);
  const expiryYearRef = useRef<HTMLInputElement>(null);
  const cvcRef = useRef<HTMLInputElement>(null);
  const [card, setCard] = useState({ name: '', number: '', month: '', year: '', cvc: '' });

  useEffect(() => {
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', close);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', close); document.body.style.overflow = previous; };
  }, [busy, onClose]);

  async function chooseMethod(value: Method) {
    setMethod(value); setError('');
    if (value !== 'coupon') setCoupon('');
    if (value === 'card' && !methods && !cardsLoading) {
      setCardsLoading(true);
      try {
        const result = await apiRequest<PaymentMethods>('/merchant/wallet/payment-methods', { query });
        setMethods(result);
        setSelectedCard(result.cards?.[0]?.saved_card_id || '');
      } catch (reason) { setError(errorMessage(reason)); }
      finally { setCardsLoading(false); }
    }
  }

  async function pollPayment(paymentId: string) {
    let latest: CardPayment = { payment_id: paymentId, status: 'pending', paid: false };
    for (let attempt = 0; attempt < 45; attempt += 1) {
      await wait(2000);
      const result = await apiRequest<{ payment: CardPayment }>(`/merchant/wallet/card-topups/${encodeURIComponent(paymentId)}`, { query: { user_id: query.user_id } });
      latest = result.payment;
      if (latest.paid || terminalStatuses.has(latest.status)) break;
    }
    return latest;
  }

  async function submitCard() {
    const available = methods || await apiRequest<PaymentMethods>('/merchant/wallet/payment-methods', { query });
    if (!available.omise?.enabled) throw new Error('ยังไม่สามารถชำระด้วยบัตรได้');
    let omiseToken = '';
    if (!selectedCard) {
      const digits = card.number.replace(/\D/g, '');
      const month = Number(card.month);
      const year = Number(card.year.length === 2 ? `20${card.year}` : card.year);
      if (!card.name.trim() || digits.length < 13 || month < 1 || month > 12 || year < new Date().getFullYear() || card.cvc.length !== securityCodeLength(card.number)) {
        throw new Error('กรุณาตรวจสอบข้อมูลบัตรให้ครบถ้วน');
      }
      omiseToken = await tokenizeCard(available.omise.public_key || '', card);
    }
    const idempotencyKey = `merchant_${query.user_id}_${Date.now()}_${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`;
    const body = {
      ...query,
      amount_points: Number(amount),
      sales_ref_id: salesRefId, package_id: packageId,
      omise_token: omiseToken,
      saved_card_id: selectedCard,
      save_card: !selectedCard && saveCard,
      return_uri: `${window.location.origin}/for-merchant/manage?tab=wallet`,
      idempotency_key: idempotencyKey,
    };
    const create = () => apiRequest<{ payment: CardPayment }>('/merchant/wallet/card-topups', { method: 'POST', body });
    let result: { payment: CardPayment };
    try { result = await create(); }
    catch (reason) { if (!(reason instanceof ApiError) || reason.status < 500) throw reason; result = await create(); }
    let payment = result.payment;
    if (payment.authorize_uri && !payment.paid) {
      const authorization = window.open(payment.authorize_uri, '_blank');
      if (authorization) authorization.opener = null;
      else window.location.assign(payment.authorize_uri);
      payment = await pollPayment(payment.payment_id);
      authorization?.close();
    }
    if (!payment.paid) throw new Error(payment.failure_message || 'ยังไม่ได้รับการยืนยันการชำระเงิน');
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    if (method === 'coupon' && coupon.length !== 6) { setError('กรุณากรอกโค้ดคูปอง 6 หลัก'); return; }
    if (method !== 'coupon' && Number(amount) <= 0) { setError('กรุณาระบุจำนวนเงิน'); return; }
    if (method === 'transfer' && !slip) { setError('กรุณาอัปโหลดสลิปโอนเงินก่อนส่งคำขอ'); return; }
    setBusy(true); setError('');
    try {
      if (method === 'card') {
        await submitCard();
        await onSuccess('เติมเงินเข้ากระเป๋าร้านสำเร็จ');
      } else {
        let slipUrl = '';
        let slipMediaId = '';
        if (method === 'transfer' && slip) {
          if (slip.size > 10 * 1024 * 1024) throw new Error('ไฟล์สลิปต้องมีขนาดไม่เกิน 10 MB');
          const uploaded = await apiUploadFile(slip, 'merchant-deposit-slips');
          slipUrl = uploaded.public_url || uploaded.signed_read_url || '';
          slipMediaId = uploaded.key || '';
        }
        const result = await apiRequest<{ deposit_request?: { amount_points?: number } }>('/merchant/wallet/deposit-requests', {
          method: 'POST',
          body: { ...query, sales_ref_id: method === 'coupon' ? '' : salesRefId, package_id: method === 'coupon' ? '' : packageId, amount_points: method === 'coupon' ? 0 : Number(amount), slip_url: slipUrl, slip_media_id: slipMediaId, ref_code: method === 'coupon' ? coupon : '', note },
        });
        const credited = Number(result.deposit_request?.amount_points || 0);
        await onSuccess(method === 'coupon' ? `ใช้คูปองสำเร็จ เติม ${credited.toLocaleString('th-TH')} บาทเข้ากระเป๋าร้านแล้ว` : 'ส่งคำขอเติมเงินสำเร็จ');
      }
      onClose();
    } catch (reason) { setError(errorMessage(reason)); }
    finally { setBusy(false); }
  }

  const bank = (wallet.bank_transfer_account && typeof wallet.bank_transfer_account === 'object' ? wallet.bank_transfer_account : {}) as Row;
  const bankNumber = String(bank.account_number || '');
  const bankName = String(bank.bank_name || '');
  const isKasikorn = /กสิกร|kbank|kasikorn/i.test(bankName);
  const accountName = String(bank.account_name || 'beaw.club Merchant Wallet');
  const requiredCvcLength = securityCodeLength(card.number);
  return <div className="merchant-topup-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && !busy && onClose()}>
    <section className="merchant-topup-modal" role="dialog" aria-modal="true" aria-labelledby="merchant-topup-title">
      <div className="merchant-topup-handle" />
      <header><div><h2 id="merchant-topup-title">เติมเงิน</h2><p>เติมเงินให้ {String(place.name)} เพื่อใช้กับแคมเปญร้านค้า</p></div><button type="button" className="merchant-topup-close" onClick={onClose} disabled={busy} aria-label="ปิด"><X /></button></header>
      <div className="merchant-topup-tabs" role="tablist">
        {([{ id: 'transfer', label: 'โอนเงิน', icon: Building2 }, { id: 'card', label: 'บัตรเครดิต', icon: CreditCard }, { id: 'coupon', label: 'โค้ดคูปอง', icon: Ticket }] as const).map(({ id, label, icon: Icon }) => <button type="button" key={id} role="tab" aria-selected={method === id} onClick={() => void chooseMethod(id)} disabled={busy}><Icon />{label}</button>)}
      </div>
      <form onSubmit={submit}>
        {method === 'transfer' && <>
          <div className="merchant-bank-card"><div className={`merchant-bank-icon${isKasikorn ? ' merchant-bank-icon--kbank' : ''}`}>{isKasikorn ? <img src="/banks/kbank.svg" alt="ธนาคารกสิกรไทย" /> : <Building2 />}</div><div><small>บัญชีสำหรับโอนเงิน</small><strong>{bankName} {bankNumber}</strong><span>{accountName}</span></div><button type="button" disabled={!bankNumber} onClick={async () => { await navigator.clipboard.writeText(bankNumber); setCopied(true); window.setTimeout(() => setCopied(false), 1600); }}><Copy />{copied ? 'คัดลอกแล้ว' : 'คัดลอก'}</button></div>
          <AmountFields amount={amount} setAmount={setAmount} />
          <label className="merchant-slip-picker"><input type="file" aria-required="true" accept="image/jpeg,image/png,application/pdf" onChange={event => { setSlip(event.target.files?.[0] || null); setError(''); }} /><ImagePlus /><span><strong>{slip ? slip.name : 'อัปโหลดสลิป *'}</strong><small>รองรับไฟล์ JPG, PNG, PDF ขนาดไม่เกิน 10 MB</small></span><b>{slip ? 'เปลี่ยนไฟล์' : 'เลือกไฟล์'}</b></label>
          <NoteField value={note} onChange={setNote} />
        </>}
        {method === 'coupon' && <><label className="merchant-topup-field"><span>โค้ดคูปอง</span><div><Ticket /><input aria-label="โค้ดคูปอง" autoFocus maxLength={6} value={coupon} placeholder="กรอกโค้ด 6 หลัก เช่น THX500" onChange={event => setCoupon(event.target.value.replace(/[^a-z0-9]/gi, '').toUpperCase())} /></div><small>ยอดเงินจะเติมตามมูลค่าของคูปอง และคูปองใช้ได้หนึ่งครั้ง</small></label><NoteField value={note} onChange={setNote} /></>}
        {method === 'card' && <>
          <AmountFields amount={amount} setAmount={setAmount} />
          {cardsLoading ? <p className="merchant-topup-loading">กำลังโหลดบัตร…</p> : <>
            {!!methods?.cards?.length && <div className="merchant-saved-cards"><strong>บัตรที่บันทึกไว้</strong>{methods.cards.map(saved => <button type="button" key={saved.saved_card_id} className={selectedCard === saved.saved_card_id ? 'active' : ''} onClick={() => setSelectedCard(saved.saved_card_id)}><CreditCard /><span>{saved.brand || 'Card'} •••• {saved.last_digits}<small>หมดอายุ {String(saved.expiration_month || '').padStart(2, '0')}/{String(saved.expiration_year || '').slice(-2)}</small></span><i /></button>)}<button type="button" className={!selectedCard ? 'active' : ''} onClick={() => setSelectedCard('')}><Plus /><span>ใช้บัตรใหม่</span><i /></button></div>}
            {!selectedCard && <div className="merchant-new-card"><label className="wide">ชื่อบนบัตร<input autoComplete="cc-name" value={card.name} onChange={e => setCard({ ...card, name: e.target.value })} /></label><label className="wide">หมายเลขบัตร<input inputMode="numeric" autoComplete="cc-number" maxLength={23} placeholder="1234 5678 9012 3456" value={card.number} onChange={e => {
              const input = e.currentTarget;
              let raw = input.value;
              let caret = input.selectionStart ?? raw.length;
              if ((e.nativeEvent as InputEvent).inputType === 'deleteContentBackward' && card.number.replace(/\D/g, '') === raw.replace(/\D/g, '') && caret > 0) {
                raw = raw.slice(0, caret - 1) + raw.slice(caret); caret--;
              }
              const digits = raw.replace(/\D/g, '').slice(0, 19);
              const count = Math.min(raw.slice(0, caret).replace(/\D/g, '').length, digits.length);
              const number = digits.match(/.{1,4}/g)?.join(' ') ?? '';
              setCard({ ...card, number, cvc: card.cvc.slice(0, securityCodeLength(number)) });
              const position = count + Math.floor(Math.max(0, count - 1) / 4);
              requestAnimationFrame(() => { if (document.activeElement === input) input.setSelectionRange(position, position); });
            }} /></label><label>เดือน MM<input inputMode="numeric" autoComplete="cc-exp-month" maxLength={2} value={card.month} onChange={e => { const month = e.target.value.replace(/\D/g, '').slice(0, 2); setCard({ ...card, month }); if (month.length === 2) expiryYearRef.current?.focus(); }} /></label><label>ปี YY<input ref={expiryYearRef} inputMode="numeric" autoComplete="cc-exp-year" maxLength={4} value={card.year} onChange={e => { const year = e.target.value.replace(/\D/g, '').slice(0, 4); setCard({ ...card, year }); if (year.length === 2 || year.length === 4) cvcRef.current?.focus(); }} /></label><label>CVC ({requiredCvcLength} หลัก)<input ref={cvcRef} type="password" inputMode="numeric" autoComplete="cc-csc" maxLength={requiredCvcLength} value={card.cvc} onChange={e => setCard({ ...card, cvc: e.target.value.replace(/\D/g, '').slice(0, requiredCvcLength) })} /></label><label className="merchant-save-card wide"><input type="checkbox" checked={saveCard} onChange={e => setSaveCard(e.target.checked)} /><span>บันทึกบัตรสำหรับครั้งถัดไป<small>Beaw เก็บเฉพาะ token และเลขท้ายบัตรผ่าน Omise</small></span></label></div>}
          </>}
        </>}
        {error && <p className="merchant-topup-error" role="alert">{error}</p>}
        <button className="merchant-topup-submit" disabled={busy || cardsLoading}>{busy ? 'กำลังดำเนินการ…' : method === 'card' ? 'ชำระด้วยบัตรเครดิต' : method === 'coupon' ? 'ใช้โค้ดคูปอง' : 'ส่งคำขอเติมเงิน'}</button>
        <p className="merchant-topup-secure"><LockKeyhole />ข้อมูลของคุณจะถูกเข้ารหัสและปลอดภัยเสมอ</p>
      </form>
    </section>
  </div>;
}

function AmountFields({ amount, setAmount }: { amount: string; setAmount: (value: string) => void }) {
  return <><label className="merchant-topup-field"><span>จำนวนเงิน</span><div><b>฿</b><input aria-label="จำนวนเงิน" inputMode="numeric" placeholder="ระบุจำนวนเงิน" value={amount} onChange={event => setAmount(event.target.value.replace(/\D/g, ''))} /></div></label><div className="merchant-amount-presets">{presets.map(value => <button type="button" className={Number(amount) === value ? 'active' : ''} key={value} onClick={() => setAmount(String(value))}>฿{value.toLocaleString()}</button>)}</div></>;
}

function NoteField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return <label className="merchant-topup-note"><span>หมายเหตุ <small>(ถ้ามี)</small></span><textarea value={value} onChange={event => onChange(event.target.value)} placeholder="เช่น เติมเงินสำหรับแคมเปญร้านค้า" /></label>;
}
